/**
 * Shared builder: PL `HalfBannerHalfProductTilesWidgetData` → domain `CustomSection`.
 *
 * Mirrors `banner-section-builders.ts` and `back-to-school-section-builders.ts`: dependency-light,
 * no JSX, so the widget can render the existing `HalfBannerHalfProductTiles` section component
 * (DS + legacy routing + product-card analytics) without duplicating the proto→domain conversion.
 *
 * Product cards are NOT embedded in the widget data — they arrive as nested `Widget[]`
 * (SINGLE_PRODUCT_CARD → productItem), enriched at PL serve time exactly like
 * PRODUCT_LIST / PRODUCT_GRID_CAROUSEL. The card shape mirrors product-grid
 * (`{ product: productItem }`, see product-grid-ds-widget.tsx).
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import type { IProductCard } from '@src/components/product-card/product-card.types'
import type { ContentfulImageForMenuItem } from '@src/quince-site-common/src/domains/contentful/ContentfulImage'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import Image from '@src/quince-site-common/src/domains/Image'

import { makeSection } from './banner-section-builders'
import { buildWidgetTrackingMetaData, protoButtonToDomain, toRichText } from './widget-utils'

import type { Widget } from '@lastbrand/storefront-proto-registry/api/v1/layoutfetch'
import type {
  HalfBannerHalfProductTilesWidgetData,
  Media,
  ProductCardData,
  WidgetChild,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/** WidgetChild(PRODUCT_CARD → productItem)[] → the domain product-card shape the section renders. */
export function childrenToProductCards(children: WidgetChild[] = []): IProductCard[] {
  return children
    .map(child => (child.data as { productItem?: ProductCardData } | undefined)?.productItem)
    .filter((product): product is ProductCardData => Boolean(product))
    .map(product => ({ product }) as unknown as IProductCard)
}

/** Widget(SINGLE_PRODUCT_CARD → productItem)[] → the domain product-card shape the section renders. */
export function widgetsToProductCards(widgets: Widget[] = []): IProductCard[] {
  return widgets
    .map(widget => (widget.data as { productItem?: ProductCardData } | undefined)?.productItem)
    .filter((product): product is ProductCardData => Boolean(product))
    .map(product => ({ product }) as unknown as IProductCard)
}

/**
 * Extract flat image objects from proto media for HeroBannerV2.
 * HeroBannerV2 (via useContentWithBackgroundImageProps) expects images: [{url, title, height, width}].
 */
function protoMediaToImages(media: Media | undefined): Record<string, unknown>[] {
  if (!media || media.type !== MediaType.IMAGE || !media.content) return []
  const c = media.content as Record<string, unknown>
  if (c.url) {
    return [c]
  }
  if (c.image && typeof c.image === 'object') {
    return [c.image as Record<string, unknown>]
  }
  return []
}

export function buildHalfBannerHalfProductTilesConfig(
  data: HalfBannerHalfProductTilesWidgetData,
  productCards: IProductCard[],
): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  const description = toRichText(data.description as Parameters<typeof toRichText>[0])

  const richTexts = [heading, description].filter(Boolean) as CustomSection['richTexts']

  const images = protoMediaToImages(data.media).map(raw =>
    Image.fromContentful(raw as unknown as ContentfulImageForMenuItem),
  ) as unknown as CustomSection['images']

  return makeSection({
    id: data.id,
    sectionType: CUSTOM_SECTION_TYPE.HALF_BANNER_HALF_PRODUCT_TILES,
    images,
    richTexts,
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    productCards,
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}
